import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import DeleteIcon from '@mui/icons-material/Delete';

import GratiPageWrapper, { WrapperAction } from '../components/common/GratiPageWrapper';
import { useApp } from '../contextProviders/AppProvider';
import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import useLeague from '../dataHooks/useLeague';
import useLeagueDeletionImpact from '../dataHooks/useLeagueDeletionImpact';
import { useLeagueMutators } from '../dataMutators/useLeagueMutators';

export default function DeleteLeaguePage(): ReactElement {
  const { orgId, leagueId } = useParams();
  const navigate = useNavigate();
  const { setPageName } = useApp();
  const { isGod } = useAuth();
  const { isOrgAdmin } = useData();
  const { deleteLeague } = useLeagueMutators();

  const { league, isLeagueLoading } = useLeague({ orgId, leagueId, once: true });
  const { counts, isLoading: isCountsLoading } = useLeagueDeletionImpact({ orgId, leagueId });

  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setPageName('Delete league');
  }, [setPageName]);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleConfirmDelete = useCallback(async () => {
    if (!league || isDeleting) return;
    setIsDeleting(true);
    await deleteLeague(league);
    navigate(`/org-leagues/${orgId}`);
  }, [league, isDeleting, deleteLeague, navigate, orgId]);

  const handleHardDelete = useCallback(async () => {
    if (!league || isDeleting) return;
    setIsDeleting(true);
    await deleteLeague(league, true);
    navigate(`/org-leagues/${orgId}`);
  }, [league, isDeleting, deleteLeague, navigate, orgId]);

  const validateUser = useCallback(() => {
    if (!orgId) return false;
    return isOrgAdmin(orgId) ?? false;
  }, [orgId, isOrgAdmin]);

  const totalCount = counts.seasons + counts.events + counts.teams + counts.memberRequests + counts.members;

  const isCatchAll = league?.isCatchAll ?? false;

  // Build page actions
  const pageActions = useMemo((): WrapperAction => ({
    actions: (
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <Button
          size="small"
          variant="text"
          color="secondary"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleConfirmDelete}
          disabled={isCatchAll || isCountsLoading || isDeleting}
        >
          Delete League
        </Button>
      </Stack>
    ),
  }), [handleCancel, handleConfirmDelete, isCatchAll, isCountsLoading, isDeleting]);

  // Build god actions
  const godActions = useMemo((): WrapperAction | null => {
    if (!isGod) return null;
    return {
      actions: (
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={handleHardDelete}
          disabled={isCountsLoading || isDeleting}
        >
          Hard Delete
        </Button>
      ),
    };
  }, [isGod, handleHardDelete, isCountsLoading, isDeleting]);

  return (
    <GratiPageWrapper
      isContentLoading={isLeagueLoading}
      validateUser={validateUser}
      pageActions={pageActions}
      godActions={godActions}
    >
      {league && (
        <Box sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <Typography variant="h5">
              Delete {league.name}?
            </Typography>

            {isCatchAll && (
              <Alert severity="warning">
                Catch-all leagues cannot be deleted.
              </Alert>
            )}

            {!isCatchAll && (
              <>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Proceeeding will delete the league, it&apos;s seasons, teams, divisions and events. It will not impact other leagues, or
                  the organization it is a part of. 
                </Typography>

                {isCountsLoading ? (
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Loading impact summary...
                  </Typography>
                ) : totalCount === 0 ? (
                  <Alert severity="info">
                    This league has no associated data.
                  </Alert>
                ) : (
                  <Paper variant="outlined">
                    <List dense>
                      {counts.members > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`The league has ${counts.members} member${counts.members > 1 ? 's' : ''} that will be removed from the league. It will not affect a player's data that is used in other leagues.`}
                          />
                        </ListItem>
                      )}
                      {counts.seasons > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`The league has ${counts.seasons} season${counts.seasons > 1 ? 's' : ''} that will be deleted.`}
                          />
                        </ListItem>
                      )}
                      {counts.events > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`The league has ${counts.events} event${counts.events > 1 ? 's' : ''} that will be deleted.`}
                          />
                        </ListItem>
                      )}
                      {counts.teams > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={counts.teams === 1 ? 'The league has a team that will be deleted.' : `The league has ${counts.teams} teams that will be deleted.`}
                          />
                        </ListItem>
                      )}
                      {counts.memberRequests > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={counts.memberRequests === 1 ? 'The league has a member request that will be deleted.' : `The league has ${counts.memberRequests} member requests that will be deleted.`}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}
              </>
            )}
          </Stack>
        </Box>
      )}
    </GratiPageWrapper>
  );
}
